import cloneDeep from "lodash/cloneDeep";
import fromPairs from "lodash/fromPairs";
import map from "lodash/map";
import { FormFieldData, Sections } from "PFApp/types/activity_form/activity_form_schema";
import { PropsWithChildren, useState } from "react";
import { createStore } from "zustand";

import { ConfirmationModals } from "../activity_form.types";
import { ActivityFormState, ActivityFormStoreType, Modals } from "./activity_form_store.types";
import { ActivityFormStoreContext } from "./activity_form_store_context";

type ActivityEditStoreProviderProps = {
  initialState: Pick<
    ActivityFormState,
    | "template"
    | "activity"
    | "activityStatus"
    | "activityCreator"
    | "parentActivity"
    | "subtemplates"
    | "attachments"
  >;
} & PropsWithChildren;

export const ActivityFormStoreProvider = ({
  children,
  initialState: {
    template,
    activity,
    activityStatus,
    activityCreator,
    parentActivity,
    subtemplates,
    attachments
  }
}: ActivityEditStoreProviderProps) => {
  const [store] = useState(
    createStore<ActivityFormStoreType>()((set) => ({
      formSchema: null, // will be set later in ActivityForm
      currentSectionId: Sections.BasicInformation,
      template,
      activity,
      activityStatus,
      activityCreator,
      parentActivity,
      subtemplates,
      attachments,
      qaIdPrefix: `activity-${activity.id ? "edit" : "create"}`,
      submitOptions: {},
      modals: fromPairs(
        map(
          [
            ConfirmationModals.NoRequirements,
            ConfirmationModals.Misalignment,
            ConfirmationModals.Public,
            ConfirmationModals.QuestionsChanged
          ],
          (modal) => [modal, { isDisplayed: false, isConfirmed: false }]
        )
      ) as Modals,
      cachedFrameworksValues: [],
      actions: {
        setFormSchema: (formSchema: ActivityFormStoreType["formSchema"]) => set({ formSchema }),
        setCurrentSectionId: (currentSectionId: Sections) => set({ currentSectionId }),
        setSubmitOptions: (submitOptions: ActivityFormStoreType["submitOptions"]) => set({ submitOptions }),
        setModalDisplayed: (key: ConfirmationModals, value: boolean) =>
          set((state) => ({
            modals: { ...state.modals, [key]: { ...state.modals[key], isDisplayed: value } }
          })),
        setModalConfirmed: (key: ConfirmationModals, value: boolean) =>
          set((state) => ({
            modals: { ...state.modals, [key]: { ...state.modals[key], isConfirmed: value } }
          })),
        // Saving SF values in state is needed to avoid values conflicts solving once again
        // when Engagement or subtemplate is changed
        saveFrameworksValuesInCache: (values: FormFieldData[]) =>
          set({ cachedFrameworksValues: cloneDeep(values) })
      }
    }))
  );

  return <ActivityFormStoreContext.Provider value={store}>{children}</ActivityFormStoreContext.Provider>;
};
